import { atom, selector, selectorFamily } from 'recoil';
import { filteredInstructorIds, filteredInstructors } from './instructorState';
export const streamsState = atom({
	key: 'streamsStateAtomKey',
	default: {} as {
		[streamId: string]: OT.Stream;
	},
	dangerouslyAllowMutability: true,
});

export const connectingToStreamState = atom({
	key: 'connectingToStreamStateAtomKey',
	default: false,
});

export const screenStreamsState = atom({
	key: 'screenStreamsStateAtomKey',
	default: {} as { [streamId: string]: OT.Stream },
	dangerouslyAllowMutability: true,
});

export const connectedStreamsState = atom({
	key: 'connectedStreamsStateAtomKey',
	default: [] as string[],
});

export const connectedStudentsState = atom<string | undefined>({
	key: 'connectedStudentsStateAtomKey',
	default: undefined,
});

const boardPinnedStreamsLocalStorage = selector({
	key: 'boardPinnedStreamsLocalStorageSelector',
	get: async () => {
		// 'pinnedUsers' is the legacy key — migrate on first read
		const pinnedUsersJson =
			localStorage.getItem('boardPinnedUsers') ?? localStorage.getItem('pinnedUsers');
		if (pinnedUsersJson) {
			localStorage.removeItem('pinnedUsers');
			const parsed = JSON.parse(pinnedUsersJson);
			return parsed;
		}
		return [];
	},
});

export const boardPinnedStreamsState = atom({
	key: 'boardPinnedStreamsStateAtomKey',
	default: boardPinnedStreamsLocalStorage,
});

// Migration: clear legacy per-session video pin persistence (deprecated 2026-04)
if (typeof window !== 'undefined') {
	localStorage.removeItem('videoPinnedFeeds');
}

export const videoPinnedStreamsState = atom<string[]>({
	key: 'videoPinnedStreamsStateAtomKey',
	default: [],
});

// Force-hidden video feeds (session-only, not persisted)
export const videoHiddenStreamsState = atom({
	key: 'videoHiddenStreamsStateAtomKey',
	default: [] as string[],
});

// Tiles the instructor has locked (session-only); default unlocked
export const videoLockedStreamsState = atom({
	key: 'videoLockedStreamsStateAtomKey',
	default: [] as string[],
});

export const streamStateFilter = atom({
	key: 'streamStateFilterAtomKey',
	default: undefined,
});

export const instructorStreamsState = atom({
	key: 'instructorStreamsAtomKey',
	default: [] as string[],
});

export const allUsersForInstructors = selector({
	key: 'UsersForInstructorsSelectorKey',
	get: ({ get }) => {
		const streams = get(streamsState);
		const connectedStreams = get(connectedStreamsState);
		const videoPinned = get(videoPinnedStreamsState);
		const videoHidden = get(videoHiddenStreamsState);
		const invisibleInstructors = get(filteredInstructorIds('invisible'));
		const streamIds = Object.keys(streams);
		const filteredConnectedStreams = connectedStreams.filter((id) => !invisibleInstructors.includes(id));

		const pinnedVisible = videoPinned.filter(
			(id: string) => streamIds.includes(id) && !videoHidden.includes(id)
		);
		const connectedNewestFirst = [...filteredConnectedStreams].reverse().filter(
			(id: string) => streamIds.includes(id) && !videoHidden.includes(id) && !videoPinned.includes(id)
		);
		const result = (Array.from(new Set([...pinnedVisible, ...connectedNewestFirst])) as string[]).slice(0, 8);
		return result;
	},
	dangerouslyAllowMutability: true,
	cachePolicy_UNSTABLE: {
		eviction: 'most-recent',
	},
});

export const selectStreamForUser = selectorFamily({
	key: 'selectStreamForUserKey',
	get:
		(userId: string) =>
		({ get }) => {
			const streams = get(streamsState);
			if (userId) {
				return streams[userId];
			}
			return null;
		},
	dangerouslyAllowMutability: true,
	cachePolicy_UNSTABLE: {
		eviction: 'most-recent',
	},
});
